// Global barcode/QR scanner detector.
//
// USB barcode scanners (and most retail QR readers) act as keyboard wedges:
// they "type" the captured code as fast keystrokes and finish with Enter.
// We listen on the document and separate scanner input from regular typing
// with timing + length gates (see src/lib/scanBurst.core.ts — unit tested):
//
//   1. **Timing**: characters from a scanner arrive in a tight burst
//      (typically < 30 ms between chars). Anything slower than
//      `avgTimeByChar` ms/char on average is treated as human typing.
//   2. **Length + terminator**: at least `minLength` chars ending with
//      Enter, within a hard total-duration cap.
//
// Defaults mirror the legacy waio-portal scanner-listener (which wrapped
// jquery.scannerdetection): minLength=6, avgTimeByChar=50ms, endChar=Enter.
//
// CAPTURE-FROM-INPUT (docs/scan-focus-plan.md): when a text input is
// focused, the burst still accumulates — alongside the input element and a
// snapshot of its value taken before the first burst char. A qualifying
// burst is then treated as a scan: the Enter is swallowed, the input is
// restored to its pre-burst value and blurred, and onScan dispatches.
// Human-speed typing fails the gates and behaves exactly as before —
// silently, so normal search-and-Enter never logs or side-effects. This is
// what makes "tap search, change mind, scan" work on a POS; blur hygiene
// alone can't (the cashier legitimately parked focus there).
// Contenteditable elements stay fully inert — their content can't be
// restored with the input-value mechanic.
//
// Mount once at the route level. Calling `onScan` is the consumer's
// responsibility — this hook only detects, never dispatches.

import { useEffect, useRef } from 'react'
import { classifyBurst, resolveEnterAction } from '@/lib/scanBurst.core'

export interface ScanMeta {
  /** getModifierState('CapsLock') on the terminating Enter. True means
   * the OS case-inverted every scanned letter in transit — lot matching
   * uses it to order its exact-match probes (see lib/batches). */
  capsLock: boolean
}

export interface BarcodeScannerOptions {
  /** Called with the captured string when a scan completes. */
  onScan: (code: string, meta: ScanMeta) => void
  /** Min char count to qualify as a scan. Default 6 (legacy parity). */
  minLength?: number
  /** Max average ms/char to qualify as scanner input. Default 50. */
  avgTimeByChar?: number
  /** Cooldown after a successful scan before another will dispatch. Default 500. */
  cooldownMs?: number
  /** Disable detection entirely (e.g. while a blocking modal is open). */
  enabled?: boolean
}

const DEFAULT_MIN_LENGTH = 6
const DEFAULT_AVG_TIME_BY_CHAR_MS = 50
const DEFAULT_COOLDOWN_MS = 1000
// Raw scan buffers can be customer identifiers (CUST: QR payloads,
// phone-number loyalty codes), and these logs ship to PostHog via replay
// console capture in production — log content in DEV, length-only in prod.
// Length + timing stay in prod logs; they're what scan-not-working
// tickets actually need.
// Accepted scans are true barcodes — raw contents are useful for scanner
// tuning in dev, masked in prod.
const bufForLog = (buf: string) =>
  import.meta.env.DEV ? buf : `[${buf.length} chars]`
// REJECTED buffers are, by definition, likely human typing — which
// includes clerk/manager PINs keyed while the scanner listener is armed.
// Console output ships to PostHog session replay, so rejected contents
// are masked unconditionally (dev included); length + timing remain,
// which is what the rejection diagnostics actually need.
const rejectedBufForLog = (buf: string) => `[${buf.length} chars]`
// Hard upper bound on how long a single scan can take to receive,
// regardless of length. Prevents a slow typist who happens to hit Enter
// after 6 chars from triggering a false scan.
const SCAN_MAX_DURATION_MS = 1500
// A printable char arriving after this much idle time starts a NEW
// burst — the buffered chars belong to a previous act (typed search
// text, an abandoned scan). Scanners emit their whole code with tight
// inter-char gaps (well under the 50ms/char avg gate) and never pause
// mid-code, so this can't split a real scan. Without it, "type in the
// search box, pause, then scan" lumped the stale typed prefix into the
// scan's burst, classified too_slow, and went inert.
const BURST_IDLE_GAP_MS = 500

// Non-printable keys that should abort an in-flight scan. Modifier-only
// keys (Shift, CapsLock, Control, Alt, Meta, …) are deliberately
// excluded: scanners typically emit Shift keydown before every uppercase
// letter, so resetting on Shift would wipe the buffer between every
// pair of capital chars and only a short tail would survive. F-keys
// and IME signals (`Process`, `Dead`, `Unidentified`) are also benign.
// Backspace in the list doubles as the "human is editing an input"
// signal for the capture-from-input path.
const BREAKING_KEYS = new Set<string>([
  'Escape',
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Backspace',
  'Delete',
])

type RestorableInput = HTMLInputElement | HTMLTextAreaElement

/** The focused element when it's a restorable text field, else null. */
function getFocusedTextInput(): RestorableInput | null {
  const el = document.activeElement
  if (el instanceof HTMLInputElement) {
    // Checkbox/radio/button/etc don't capture text.
    const t = (el.type || '').toLowerCase()
    const textTypes = new Set([
      '', 'text', 'search', 'tel', 'email', 'url', 'password', 'number',
    ])
    return textTypes.has(t) ? el : null
  }
  if (el instanceof HTMLTextAreaElement) return el
  return null
}

function isContentEditableFocused(): boolean {
  const el = document.activeElement
  return el instanceof HTMLElement && el.isContentEditable
}

/**
 * Blur the focused element if it's a text field. Used by posStore's
 * setActiveTab as the "no text input keeps focus across a tab change"
 * invariant, and available to any flow that wants to return the till to
 * scan posture.
 */
export function blurActiveTextInput(): void {
  // Callable from store actions, which unit tests may run without a DOM.
  if (typeof document === 'undefined') return
  const el = getFocusedTextInput()
  if (el) el.blur()
  else if (isContentEditableFocused()) (document.activeElement as HTMLElement).blur()
}

// Controlled React inputs ignore direct `.value =` writes (React owns the
// value). The native prototype setter + a bubbling `input` event routes
// the restore through React's onChange, so component/store state follows.
function restoreInputValue(el: RestorableInput, value: string): void {
  const proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  setter?.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

export function useBarcodeScanner({
  onScan,
  minLength = DEFAULT_MIN_LENGTH,
  avgTimeByChar = DEFAULT_AVG_TIME_BY_CHAR_MS,
  cooldownMs = DEFAULT_COOLDOWN_MS,
  enabled = true,
}: BarcodeScannerOptions) {
  // Refs survive re-renders without re-binding the listener. We avoid
  // putting the buffer in state to keep it off the React render loop —
  // a scan fires dozens of keydowns in tens of ms.
  const bufferRef = useRef('')
  const firstCharAtRef = useRef(0)
  const lastCharAtRef = useRef(0)
  const lastScanAtRef = useRef(0)
  const onScanRef = useRef(onScan)
  // Capture-from-input burst context: the text field the burst is landing
  // in (null for the classic unfocused path) and its value BEFORE the
  // first burst char, so an accepted scan can be surgically undone.
  const burstElRef = useRef<RestorableInput | null>(null)
  const burstSnapshotRef = useRef<string | null>(null)

  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  useEffect(() => {
    if (!enabled) return

    const reset = () => {
      bufferRef.current = ''
      firstCharAtRef.current = 0
      lastCharAtRef.current = 0
      burstElRef.current = null
      burstSnapshotRef.current = null
    }

    const seedBurstContext = (focusedInput: RestorableInput | null) => {
      burstElRef.current = focusedInput
      // Snapshot on keydown — the char hasn't been applied to .value yet.
      burstSnapshotRef.current = focusedInput ? focusedInput.value : null
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Modifier-key combos are shortcuts, not scans.
      if (e.ctrlKey || e.metaKey || e.altKey) return reset()

      const focusedInput = getFocusedTextInput()
      // Verbose per-keystroke diagnostic — only logs printable keys
      // and Enter so we don't drown the console in modifier presses.
      // DEV builds only (Vite strips this in prod): in production it
      // would ship every keystroke — including typing into masked
      // inputs — to PostHog via replay console capture.
      if (import.meta.env.DEV && (e.key.length === 1 || e.key === 'Enter')) {
        console.log('[scanner] key', {
          key: e.key,
          focused: (document.activeElement?.tagName || 'NONE').toUpperCase(),
          focusedTextInput: focusedInput !== null,
          bufferBefore: bufferRef.current,
        })
      }

      // Contenteditable can't be value-restored — stay fully inert, as
      // the whole hook was for any focused field before capture-from-
      // input landed (docs/scan-focus-plan.md).
      if (isContentEditableFocused()) return reset()

      const now = performance.now()

      // Terminator → evaluate.
      if (e.key === 'Enter') {
        const buf = bufferRef.current
        const start = firstCharAtRef.current
        const end = lastCharAtRef.current
        const burstEl = burstElRef.current
        const burstSnapshot = burstSnapshotRef.current
        reset()

        const verdict = classifyBurst(buf.length, end - start, {
          minLength,
          avgTimeByCharMs: avgTimeByChar,
          maxDurationMs: SCAN_MAX_DURATION_MS,
        })
        // Restore is only coherent when the whole burst landed in the
        // field that's still focused. A boundary focus change means the
        // chars never reached this input — dispatch without touching it.
        const restorable =
          focusedInput !== null
          && burstEl === focusedInput
          && burstSnapshot !== null
        const sinceLast = now - lastScanAtRef.current

        // The branch matrix lives in scanBurst.core (unit tested); this
        // layer just executes the returned action against the DOM.
        const action = resolveEnterAction({
          verdict,
          inInput: focusedInput !== null,
          restorable,
          cooldownHit:
            verdict === 'scan'
            && lastScanAtRef.current > 0
            && sinceLast < cooldownMs,
        })

        if (action.swallowEnter) {
          // Keep the Enter from triggering form submits / button clicks
          // on whatever happens to be focused.
          e.preventDefault()
          e.stopPropagation()
        }
        if (action.restoreInput && focusedInput !== null && burstSnapshot !== null) {
          restoreInputValue(focusedInput, burstSnapshot)
        }
        if (action.blurInput && focusedInput !== null) {
          // Scanning signals scan posture: release focus so follow-up
          // scans take the clean unfocused path (and the cashier isn't
          // left "typing into" a box they never meant to fill).
          focusedInput.blur()
        }

        switch (action.log) {
          case 'too_short':
            if (buf.length > 0) {
              console.log('[scanner] rejected: too short', { buf: rejectedBufForLog(buf), length: buf.length, minLength })
            }
            break
          case 'too_slow':
            console.log('[scanner] rejected: too slow (looks like typing)', {
              buf: rejectedBufForLog(buf),
              length: buf.length,
              elapsedMs: Math.round(end - start),
              allowedMs: Math.round(buf.length * avgTimeByChar),
            })
            break
          case 'cooldown':
            console.log('[scanner] rejected: cooldown', {
              buf: bufForLog(buf),
              sinceLastMs: Math.round(sinceLast),
              cooldownMs,
            })
            break
          case 'captured-from-input':
            console.log('[scanner] captured-from-input', {
              buf: bufForLog(buf),
              length: buf.length,
              elapsedMs: Math.round(end - start),
              target: focusedInput?.placeholder || focusedInput?.name || focusedInput?.tagName,
            })
            break
          case 'accepted':
            console.log('[scanner] accepted', { buf: bufForLog(buf), length: buf.length, elapsedMs: Math.round(end - start) })
            break
        }

        if (action.dispatch) {
          lastScanAtRef.current = now
          // CapsLock state read off the terminator keydown — the same
          // keyboard stream the burst arrived on, milliseconds later, so
          // it reflects the state the letters were typed under.
          onScanRef.current(buf, { capsLock: e.getModifierState('CapsLock') })
        }
        return
      }

      // Single printable character → append, tracking which field (if
      // any) the burst is landing in. A focus change mid-burst means the
      // human moved around — whatever accumulated can't be one scan, so
      // the current char starts a fresh burst in the new context.
      if (e.key.length === 1) {
        // Idle-gap boundary: chars buffered longer than BURST_IDLE_GAP_MS
        // ago can't be part of the burst starting now — drop them so a
        // scan after abandoned typing classifies on its own timing. The
        // re-seed below then snapshots the input INCLUDING the earlier
        // typed text, so a captured scan restores exactly what the
        // cashier had typed before the pause.
        if (
          bufferRef.current !== ''
          && now - lastCharAtRef.current > BURST_IDLE_GAP_MS
        ) {
          reset()
        }
        if (bufferRef.current === '') {
          seedBurstContext(focusedInput)
        } else if (burstElRef.current !== focusedInput) {
          reset()
          seedBurstContext(focusedInput)
        }
        if (firstCharAtRef.current === 0) firstCharAtRef.current = now
        lastCharAtRef.current = now
        bufferRef.current += e.key
        return
      }

      // Reset only on keys that mean "the user is doing something
      // else" mid-scan. Critically, modifier-only keys (Shift,
      // CapsLock, Control, …) must NOT reset — most scanners emit a
      // Shift keydown before every uppercase letter, which would
      // otherwise wipe the buffer between chars and only the trailing
      // lowercase tail would survive. F-keys, IME `Process`/`Dead`,
      // and `Unidentified` are also benign — leave the buffer alone
      // and let timing + length validate at Enter time.
      if (BREAKING_KEYS.has(e.key)) reset()
    }

    // Capture phase so we see the keystrokes before any focused widget's
    // own handler. Combined with `preventDefault` on the terminator, this
    // keeps an in-flight scan from leaking into whatever element happens
    // to be focused (e.g. a button steals the Enter for a click).
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [enabled, minLength, avgTimeByChar, cooldownMs])
}
